import React, { useEffect, useState, useRef } from 'react';
import { SearchIcon, XIcon } from 'lucide-react';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import { useBrand } from '../../hooks/useBrand';
export interface Suggestion {
  id: string;
  label: string;
  sublabel?: string;
  type?: string;
  image?: string;
  /** Optional bucket name. Consecutive suggestions sharing a group render under a sticky group heading. */
  group?: string;
}
interface SearchWithAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSelect?: (suggestion: Suggestion) => void;
  onSearch?: (query: string) => void;
  suggestions: Suggestion[];
  placeholder?: string;
  className?: string;
  variant?: 'default' | 'rounded' | 'pill';
  showIcon?: boolean;
  mobileFullscreen?: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
}
export const SearchWithAutocomplete: React.FC<SearchWithAutocompleteProps> = ({
  value,
  onChange,
  onSelect,
  onSearch,
  suggestions,
  placeholder = 'Search...',
  className = '',
  variant = 'default',
  showIcon = true,
  mobileFullscreen = false,
  autoFocus = false,
  disabled = false
}) => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const {
    theme
  } = useBrand();
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Filter suggestions based on input value if not handled by parent
  // Note: In a real app, filtering might happen server-side or in parent
  const filteredSuggestions = suggestions.filter(item => item.label.toLowerCase().includes(value.toLowerCase()));
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  useEffect(() => {
    if (value && filteredSuggestions.length > 0) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
    setHighlightedIndex(-1);
  }, [value, filteredSuggestions.length]);
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => prev < filteredSuggestions.length - 1 ? prev + 1 : prev);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0 && filteredSuggestions[highlightedIndex]) {
        handleSelect(filteredSuggestions[highlightedIndex]);
      } else if (onSearch) {
        onSearch(value);
        setIsOpen(false);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };
  const handleSelect = (suggestion: Suggestion) => {
    if (onSelect) {
      onSelect(suggestion);
    } else {
      onChange(suggestion.label);
    }
    setIsOpen(false);
  };
  const getVariantClasses = () => {
    if (mobileFullscreen) return 'rounded-none border-0 border-b';
    switch (variant) {
      case 'rounded':
        return 'rounded-md';
      case 'pill':
        return 'rounded-full';
      default:
        return 'rounded-md';
    }
  };
  return <div ref={wrapperRef} className={`relative w-full ${className}`} data-id="element-3881" data-mp="klbdx1kfszdr">
      <div className="relative" data-id="element-3882" data-mp="fn9xypib5g6i">
        {showIcon && <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3883" data-mp="2eysvm7y5v20v">
            <SearchIcon className={`h-5 w-5 ${dk.textMuted}`} data-id="element-3884" data-mp="2a097q200bg5v" />
          </div>}
        <input ref={inputRef} type="text" className={`block w-full ${showIcon ? 'pl-10' : 'pl-4'} pr-10 py-2 border ${mobileFullscreen ? `${dk.dropdown} ${dk.textPrimary} placeholder-gray-400 py-4 text-base` : `${dk.inputOnCard} ${rt.inputFocus}`} ${getVariantClasses()} focus:outline-none ${mobileFullscreen ? '' : 'focus:ring-2'} sm:text-sm transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`} placeholder={placeholder} value={value} onChange={e => !disabled && onChange(e.target.value)} onFocus={() => {
        if (!disabled && value && filteredSuggestions.length > 0) setIsOpen(true);
      }} onKeyDown={!disabled ? handleKeyDown : undefined} disabled={disabled} data-id="element-3885" data-mp="16okcxqdvf707" />
        {value && <button onClick={() => {
        onChange('');
        setIsOpen(false);
        inputRef.current?.focus();
      }} className={`absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-500 ${mobileFullscreen ? 'touch-target' : ''}`} data-id="element-3886" data-mp="511h66j1oldj">
            <XIcon className="h-4 w-4" data-id="element-3887" data-mp="3hlf98x8jmghw" />
          </button>}
      </div>

      {(isOpen || mobileFullscreen) && filteredSuggestions.length > 0 && <div className={`${mobileFullscreen ? `w-full bg-transparent mt-0` : `absolute z-50 mt-1 w-full shadow-lg max-h-60 rounded-md py-1 ring-1 ring-black ring-opacity-5 overflow-auto border ${dk.dropdown}`}`} data-id="element-3888" data-mp="3rggvnlbfp4gs">
          {filteredSuggestions.map((suggestion, index) => {
        const previousGroup = index > 0 ? filteredSuggestions[index - 1].group : undefined;
        const showGroupHeading = !!suggestion.group && suggestion.group !== previousGroup;
        return <React.Fragment key={suggestion.id} data-id="element-3889" data-mp="s2n0uc32bl90" data-mpi={index}>
                {showGroupHeading && <div role="presentation" className={`sticky top-0 z-10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide ${index > 0 ? 'border-t' : ''} ${dk.badgeNeutral} ${dk.border}`} data-id="element-3890" data-mp="o2ml8o48940d">
                    {suggestion.group}
                  </div>}
                <div className={`cursor-pointer select-none relative flex items-center ${mobileFullscreen ? 'py-3 px-4 border-b border-gray-100 dark:border-gray-800 touch-target-lg' : 'py-2 pl-3 pr-9'} ${index === highlightedIndex ? rt.activeTab : `${dk.textPrimary} ${dk.dropdownItemHover}`}`} onClick={() => handleSelect(suggestion)} onMouseEnter={() => setHighlightedIndex(index)} data-id="element-3891" data-mp="19sm8tpb6z9n7">
                  {suggestion.image && <img src={suggestion.image} alt="" className={`${mobileFullscreen ? 'h-10 w-10' : 'h-8 w-8'} rounded-md object-cover mr-3 flex-shrink-0`} data-id="element-3892" data-mp="3pka31cytmo2b" />}
                  <div className="flex flex-col min-w-0" data-id="element-3893" data-mp="2bpa6vnjtsb7i">
                    <span className={`block truncate ${index === highlightedIndex ? 'font-semibold' : 'font-normal'} ${mobileFullscreen ? 'text-base' : 'text-sm'}`} data-id="element-3894" data-mp="26x0l8jpqtxb1">
                      {suggestion.label}
                    </span>
                    {suggestion.sublabel && <span className={`block truncate text-xs ${dk.textMuted}`} data-id="element-3895" data-mp="2umbc99hakdbz">
                        {suggestion.sublabel}
                      </span>}
                  </div>
                  {suggestion.type && <span className={`ml-auto text-xs px-2 py-0.5 ${theme.tagRadius} ${dk.badgeNeutral}`} data-id="element-3896" data-mp="2gmabdu1115wi">
                      {suggestion.type}
                    </span>}
                </div>
              </React.Fragment>;
      })}
        </div>}
    </div>;
};